import React, { useState, useEffect, useRef } from "react";
import { Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import { login as storeAuth } from "../../utils/auth";
import { useBranding } from "../../contexts/BrandingContext";
import {
  resolveBrandName,
  resolveBrandLogo,
  resolveBrandFavicon,
} from "../../constants/brand";
import lmsLogo from "../../Assets/Images/Logos/lms_logo.png";
import lmsFavIcon from "../../Assets/Images/Logos/favicon.png";
import { BASE_DOMAIN } from "../../utils/brandHost";
import {
  Building2,
  User,
  Mail,
  Phone,
  Lock,
  Eye,
  EyeOff,
  CheckCircle2,
  Rocket,
  ShieldCheck,
  ArrowRight,
  ArrowLeft,
  Globe,
  XCircle,
  MonitorPlay,
  Wallet,
  ClipboardCheck,
  Smartphone,
} from "lucide-react";
import AuthLayout from "./AuthLayout";
import AuthStepProgress from "./AuthStepProgress";
import {
  AppButton,
  Callout,
  Checkbox,
  FormField,
  IconButton,
  Input,
  Label,
  OtpInput,
  cn,
} from "../ui";

// This page is where an institute is *created*, so there is no institute whose
// branding it could legitimately wear. BrandingContext still resolves something
// here — a stale localStorage entry from a previously visited subdomain, or a
// logoUrl pointing at an R2 object that no longer exists (a broken <img> right
// beside the headline) — so the platform brand is pinned instead of read from
// it. The resolveBrand* wrappers keep an enterprise build on its own mark.
const PLATFORM_NAME = resolveBrandName("LMS Campus");
const PLATFORM_LOGO = resolveBrandLogo(lmsLogo);
const PLATFORM_FAVICON = resolveBrandFavicon(lmsFavIcon);
const PLATFORM_SUPPORT_EMAIL = "support@lmscampus.com";
const PORTAL_DOMAIN = BASE_DOMAIN;

const STEPS = {
  PHONE: 1,
  OTP: 2,
  DETAILS: 3,
};
const STEP_COUNT = 3;

// Heading and sub-line for each step, in one table so the two cannot drift apart
// — the same shape ForgotPassword uses for its three steps.
const STEP_COPY = {
  [STEPS.PHONE]: {
    title: "Start your free trial",
    sub: "Verify your phone number to get started",
  },
  [STEPS.OTP]: {
    title: "Verify your number",
    sub: "Enter the 6-digit code we just sent you",
  },
  [STEPS.DETAILS]: {
    title: "Set up your institute",
    sub: "Almost there — just a few details",
  },
};

// The brand panel's copy. This screen sells the platform to the person buying
// it, where /login and /signup address a learner, so none of it is the default.
const REGISTER_HIGHLIGHTS = [
  {
    icon: MonitorPlay,
    title: "Live Classes",
    desc: "Run and auto-record every session",
  },
  {
    icon: Wallet,
    title: "Fees & Invoices",
    desc: "Collect online, track dues, issue receipts",
  },
  {
    icon: ClipboardCheck,
    title: "Assessments",
    desc: "Quizzes, coding practice & AI mock interviews",
  },
  {
    icon: Smartphone,
    title: "Your Own Portal",
    desc: "Branded subdomain and a student mobile app",
  },
];

const REGISTER_STATS = [
  { value: "2 min", label: "to go live" },
  { value: "No card", label: "required" },
  { value: "SSL", label: "secured" },
];

const REGISTER_TITLE = (
  <>
    Run your institute
    <br />
    <span className="tw-text-primary">beautifully.</span>
  </>
);

const REGISTER_BLURB =
  "The all-in-one workspace to manage courses, batches, live classes, fees, " +
  "assessments and student progress — built for modern training institutes.";

// A distributor's referral link: /register-institute?ref=CODE. The code is kept
// for 30 days, so someone who opens the link today and signs up next week is
// still attached to the distributor who sent it. Every storage access is
// guarded — private windows and blocked site data throw.
const REFERRAL_KEY = "vc-referral";
const REFERRAL_DAYS = 30;

function readStoredReferral() {
  try {
    const raw = window.localStorage.getItem(REFERRAL_KEY);
    if (!raw) return "";
    const { code, at } = JSON.parse(raw);
    if (!code || !at || Date.now() - at > REFERRAL_DAYS * 86400000) return "";
    return String(code);
  } catch {
    return "";
  }
}
function storeReferral(code) {
  try {
    if (code) window.localStorage.setItem(REFERRAL_KEY, JSON.stringify({ code, at: Date.now() }));
    else window.localStorage.removeItem(REFERRAL_KEY);
  } catch { /* storage unavailable — the code still rides this visit */ }
}

const RegisterInstitute = () => {
  // Only refreshBranding is taken from the context; the marks below are the
  // platform's, not the visited host's — see PLATFORM_* above.
  const { refreshBranding } = useBranding();
  const navigate = useNavigate();

  const [step, setStep] = useState(STEPS.PHONE);
  const [form, setForm] = useState({
    instituteName: "",
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    subdomain: "",
  });
  const [otp, setOtp] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ tone: "", text: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  // Deadline (epoch ms) by which registration must be submitted. Verifying the
  // phone does not finish anything on its own — the backend keeps the verified
  // OTP record only until this moment and rejects the submit afterwards — so
  // the details step counts it down instead of letting it lapse silently.
  const [verifyDeadline, setVerifyDeadline] = useState(null);
  // The clock is a plain "now" the ticker advances; the remaining time is
  // derived from it during render. Deriving rather than storing a countdown
  // matters twice over: the first render after verification already shows the
  // right number instead of a frame of 0 ("expired"), and a throttled
  // background tab resumes at the true remaining time rather than wherever a
  // decrementing counter stalled.
  const [now, setNow] = useState(() => Date.now());
  const [subdomainStatus, setSubdomainStatus] = useState({
    checking: false,
    available: null,
    message: "",
  });
  const subdomainTimerRef = useRef(null);

  const verifySecondsLeft = verifyDeadline
    ? Math.max(0, Math.ceil((verifyDeadline - now) / 1000))
    : 0;
  const verificationExpired = verifyDeadline !== null && verifySecondsLeft <= 0;
  const mismatch = Boolean(form.confirmPassword) && form.password !== form.confirmPassword;
  // Set only when a referral code resolves to an active distributor. A client of
  // a distributor has no trial and sees no pricing, so the trial wording goes.
  const [referrer, setReferrer] = useState(null); // { code, name }
  const copy = step === STEPS.PHONE && referrer
    ? { title: "Create your institute", sub: "Verify your phone number to get started" }
    : STEP_COPY[step];

  useEffect(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("ref") || "";
    const code = fromUrl.trim() || readStoredReferral();
    if (!code) return undefined;
    let cancelled = false;
    apiClient
      .get(`/public/distributor-ref/${encodeURIComponent(code)}`)
      .then(({ data }) => {
        if (cancelled || !data?.name) return;
        setReferrer({ code, name: data.name });
        storeReferral(code);
      })
      .catch(() => {
        // Unknown, paused or rotated — an ordinary sign-up, and forget it.
        if (!cancelled) storeReferral("");
      });
    return () => { cancelled = true; };
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  // Tick the clock while a verification deadline is pending, and stop once it
  // has passed — nothing below the deadline re-renders after that.
  useEffect(() => {
    if (!verifyDeadline) return;
    setNow(Date.now());
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= verifyDeadline) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [verifyDeadline]);

  const formatCountdown = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60);
    const sec = totalSeconds % 60;
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  // Send the caller back to step 1 with a clean slate. Used both by the "verify
  // again" button and by a submit the backend rejected as expired.
  const restartVerification = (text) => {
    setVerifyDeadline(null);
    setPhoneVerified(false);
    setOtp("");
    setStep(STEPS.PHONE);
    setMsg(text ? { tone: "danger", text } : { tone: "", text: "" });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "subdomain") {
      const cleaned = value.toLowerCase().replace(/[^a-z0-9-]/g, "").replace(/^-/, "");
      setForm((prev) => ({ ...prev, subdomain: cleaned }));
      setSubdomainStatus({ checking: false, available: null, message: "" });
      // Debounced availability check
      if (subdomainTimerRef.current) clearTimeout(subdomainTimerRef.current);
      if (cleaned.length >= 3) {
        setSubdomainStatus({ checking: true, available: null, message: "Checking..." });
        subdomainTimerRef.current = setTimeout(() => checkSubdomainAvailability(cleaned), 500);
      }
    } else if (name === "phone") {
      const digitsOnly = value.replace(/[^0-9]/g, "").slice(0, 10);
      setForm((prev) => ({ ...prev, phone: digitsOnly }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
    setMsg({ tone: "", text: "" });
  };

  const checkSubdomainAvailability = async (subdomain) => {
    try {
      const { data } = await apiClient.get(
        `/auth/check-subdomain?subdomain=${encodeURIComponent(subdomain)}`
      );
      setSubdomainStatus({ checking: false, available: data.available, message: data.message });
    } catch {
      setSubdomainStatus({ checking: false, available: null, message: "Could not check availability." });
    }
  };

  // ─── Step 1: Send OTP ───────────────────────────────────────────────
  const handleSendOtp = async (e) => {
    e?.preventDefault();
    setMsg({ tone: "", text: "" });
    const cleanPhone = form.phone.replace(/[^0-9]/g, "");
    if (cleanPhone.length < 10) {
      setMsg({ tone: "danger", text: "Please enter a valid 10-digit phone number." });
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiClient.post("/auth/otp/send", {
        phone: form.phone,
        purpose: "register-institute",
      });
      setMsg({ tone: "success", text: data.message || "OTP sent!" });
      setStep(STEPS.OTP);
      setOtp("");
      setResendTimer(60);
    } catch (err) {
      setMsg({
        tone: "danger",
        text: err?.response?.data?.message || "Failed to send OTP.",
      });
    }
    setLoading(false);
  };

  // ─── Step 2: Verify OTP ─────────────────────────────────────────────
  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    if (otp.length !== 6) {
      setMsg({ tone: "danger", text: "Please enter the complete 6-digit OTP." });
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiClient.post("/auth/otp/verify", {
        phone: form.phone,
        otp,
        purpose: "register-institute",
      });
      if (data.verified) {
        // Prefer the server's absolute deadline; fall back to its stated window
        // (and finally to 10 minutes) so an older backend still gets a timer.
        const deadline = data.expiresAt
          ? new Date(data.expiresAt).getTime()
          : Date.now() + (data.expiresInMinutes || 10) * 60 * 1000;
        setNow(Date.now());
        setVerifyDeadline(Number.isFinite(deadline) ? deadline : Date.now() + 10 * 60 * 1000);
        setPhoneVerified(true);
        setMsg({ tone: "success", text: "Phone verified! Complete your details." });
        setStep(STEPS.DETAILS);
      }
    } catch (err) {
      setMsg({
        tone: "danger",
        text: err?.response?.data?.message || "OTP verification failed.",
      });
    }
    setLoading(false);
  };

  // ─── Step 3: Submit registration ────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ tone: "", text: "" });

    if (!acceptedTerms) {
      setMsg({ tone: "danger", text: "Please accept the Terms & Conditions to continue." });
      return;
    }
    if (form.password !== form.confirmPassword) {
      setMsg({ tone: "danger", text: "Passwords do not match." });
      return;
    }
    if (form.password.length < 6) {
      setMsg({ tone: "danger", text: "Password must be at least 6 characters." });
      return;
    }
    if (!phoneVerified) {
      setMsg({ tone: "danger", text: "Please verify your phone number first." });
      return;
    }
    if (verificationExpired) {
      restartVerification("Your phone verification has expired. Please verify your number again.");
      return;
    }

    setLoading(true);
    try {
      const { data } = await apiClient.post("/auth/register-institute", {
        instituteName: form.instituteName,
        name: form.name,
        email: form.email,
        phone: form.phone,
        password: form.password,
        subdomain: form.subdomain || undefined,
        referralCode: referrer?.code || undefined,
      });

      if (data.token) {
        storeReferral("");
        storeAuth(data);
        if (refreshBranding) refreshBranding();

        // If backend returned a portalUrl (subdomain), redirect there
        // so the user lands on their own institute subdomain
        const portalUrl = data.institute?.portalUrl;
        const isLocalhost = window.location.hostname === "localhost";

        setMsg({
          tone: "success",
          text: `🎉 ${data.message || "Institute created!"} ${
            portalUrl && !isLocalhost ? `Your portal: ${portalUrl}` : ""
          } Redirecting...`,
        });

        if (portalUrl && !isLocalhost) {
          // Redirect to the institute's own subdomain dashboard
          // Pass token via URL params for cross-subdomain handoff
          const tokenParam = encodeURIComponent(data.token);
          const userParam = encodeURIComponent(JSON.stringify(data.user));
          setTimeout(() => {
            window.location.href = `${portalUrl}/dashboard?_token=${tokenParam}&_user=${userParam}`;
          }, 1500);
        } else {
          // Localhost or no subdomain — stay on same origin
          setTimeout(() => navigate("/dashboard"), 1200);
        }
      }
    } catch (err) {
      const text = err?.response?.data?.message || "Registration failed. Please try again.";
      // The window can lapse between the last tick and the request landing, so
      // the server has the final say — rewind to step 1 rather than leaving a
      // filled-in form that can never be submitted.
      if (err?.response?.data?.code === "OTP_VERIFICATION_EXPIRED") {
        restartVerification(text);
      } else {
        setMsg({ tone: "danger", text });
      }
    }
    setLoading(false);
  };

  const feedback = msg.text ? (
    <Callout tone={msg.tone || "info"} role="alert">
      {msg.text}
    </Callout>
  ) : null;

  return (
    <AuthLayout
      brand={{
        name: PLATFORM_NAME,
        favicon: PLATFORM_FAVICON,
        subtitle: "Institute Management Platform",
      }}
      side={{
        kicker: "For coaching & training institutes",
        title: REGISTER_TITLE,
        blurb: REGISTER_BLURB,
        highlights: REGISTER_HIGHLIGHTS,
        stats: REGISTER_STATS,
        badge: (
          <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-2xl tw-bg-primary/15 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-primary tw-ring-1 tw-ring-primary/25">
            <CheckCircle2 size={13} /> {referrer ? `Referred by ${referrer.name}` : "14-day free trial"}
          </span>
        ),
        footnote: referrer
          ? `${referrer.name} sets up your plan — there is nothing to pay here.`
          : "No credit card required. Cancel any time during your trial.",
      }}
      mobileLink={{ to: "/login", label: "Sign In" }}
      mobileTagline="Set up your institute in 2 minutes — no card required"
      width="wide"
      footer={
        <>
          <img
            src={PLATFORM_LOGO}
            alt={PLATFORM_NAME}
            className="tw-mx-auto tw-mb-3 tw-h-7 tw-object-contain tw-opacity-60"
          />
          <p className="tw-mb-0 tw-text-xs tw-text-content-subtle">
            Need help?{" "}
            <a
              href={`mailto:${PLATFORM_SUPPORT_EMAIL}`}
              className="tw-font-medium tw-text-primary tw-no-underline hover:tw-underline"
            >
              {PLATFORM_SUPPORT_EMAIL}
            </a>
          </p>
        </>
      }
    >
      <header className="tw-mb-6 tw-text-center">
        <h1 className="tw-mb-1.5 tw-text-[1.75rem] tw-font-bold tw-tracking-[-0.02em] tw-text-content">
          {copy.title}
        </h1>
        <p className="tw-mb-0 tw-text-sm tw-text-content-muted">{copy.sub}</p>
        {referrer && (
          <p className="tw-mb-0 tw-mt-3 tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-primary/10 tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-text-primary">
            <CheckCircle2 size={13} aria-hidden="true" /> Referred by {referrer.name}
          </p>
        )}
      </header>

      <AuthStepProgress total={STEP_COUNT} current={step} className="tw-mb-7" />

      {/* ═══════ STEP 1: Phone Number ═══════ */}
      {step === STEPS.PHONE && (
        <form onSubmit={handleSendOtp} className="tw-flex tw-flex-col tw-gap-4">
          <FormField
            label="Phone Number"
            htmlFor="ri-phone"
            hint="We'll send a one-time code to verify this number."
          >
            <Input
              id="ri-phone"
              size="lg"
              type="tel"
              inputMode="numeric"
              placeholder="Enter your 10-digit phone number"
              name="phone"
              autoComplete="tel-national"
              value={form.phone}
              onChange={handleChange}
              maxLength={10}
              required
              autoFocus
              leading={<Phone size={16} />}
            />
          </FormField>

          {feedback}

          <AppButton type="submit" size="lg" className="tw-h-12 tw-w-full" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" animation="border" /> Sending code…
              </>
            ) : (
              <>
                Send OTP <ArrowRight size={17} aria-hidden="true" />
              </>
            )}
          </AppButton>
        </form>
      )}

      {/* ═══════ STEP 2: OTP Verification ═══════ */}
      {step === STEPS.OTP && (
        <form onSubmit={handleVerifyOtp} className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-flex tw-flex-col tw-gap-3">
            {/* A caption, not a <Label>: the code field is a group of six inputs
                with its own aria-label, so a <label> here would have nothing to
                point `htmlFor` at. */}
            <p className="tw-mb-0 tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-text-[0.8125rem] tw-font-semibold tw-text-content">
              <ShieldCheck size={15} className="tw-text-primary" aria-hidden="true" />
              Code sent to {form.phone}
            </p>

            <OtpInput
              value={otp}
              onChange={(v) => {
                setOtp(v);
                setMsg({ tone: "", text: "" });
              }}
              autoFocus
              disabled={loading}
              label="Phone verification code"
            />

            <div className="tw-text-center tw-text-[0.8125rem] tw-text-content-muted">
              {resendTimer > 0 ? (
                <span>
                  Resend code in <span className="tw-tabular-nums">{resendTimer}s</span>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={loading}
                  className="tw-cursor-pointer tw-rounded-sm tw-border-0 tw-bg-transparent tw-p-0 tw-font-semibold tw-text-primary tw-focus-ring hover:tw-underline"
                >
                  Resend code
                </button>
              )}
            </div>
          </div>

          {feedback}

          <div className="tw-flex tw-gap-3">
            <AppButton
              type="button"
              variant="outline"
              size="lg"
              className="tw-h-12 tw-flex-1"
              onClick={() => {
                setStep(STEPS.PHONE);
                setMsg({ tone: "", text: "" });
              }}
            >
              <ArrowLeft size={17} aria-hidden="true" /> Back
            </AppButton>
            <AppButton
              type="submit"
              size="lg"
              className="tw-h-12 tw-flex-[2]"
              disabled={loading || otp.length !== 6}
            >
              {loading ? (
                <>
                  <Spinner size="sm" animation="border" /> Verifying…
                </>
              ) : (
                <>
                  Verify <ArrowRight size={17} aria-hidden="true" />
                </>
              )}
            </AppButton>
          </div>
        </form>
      )}

      {/* ═══════ STEP 3: Institute Details ═══════ */}
      {step === STEPS.DETAILS && (
        <form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-4">
          {/* Phone verified badge + verification countdown.
              The window is the backend's, not a cosmetic nudge: once it lapses
              the verified OTP is gone and the submit is refused, so it is shown
              rather than left to surprise anyone. */}
          {verificationExpired ? (
            <Callout tone="danger" role="alert">
              <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
                <span className="tw-flex-1">
                  Verification expired. Please verify your phone again.
                </span>
                <button
                  type="button"
                  onClick={() => restartVerification()}
                  className="tw-cursor-pointer tw-rounded-sm tw-border-0 tw-bg-transparent tw-p-0 tw-font-semibold tw-text-danger tw-underline tw-focus-ring"
                >
                  Verify again
                </button>
              </span>
            </Callout>
          ) : (
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-md tw-bg-success-soft tw-px-3 tw-py-2 tw-text-[0.8125rem] tw-font-medium tw-text-success-fg">
              <CheckCircle2 size={15} aria-hidden="true" />
              <span>Phone verified: {form.phone}</span>
              {verifyDeadline && (
                <span
                  title="Time left to complete your registration"
                  className={cn(
                    "tw-ml-auto tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-2xl tw-px-2.5 tw-py-0.5 tw-font-semibold tw-tabular-nums",
                    verifySecondsLeft <= 60
                      ? "tw-bg-danger-soft tw-text-danger-fg"
                      : "tw-bg-success tw-text-on-status"
                  )}
                >
                  <ShieldCheck size={11} aria-hidden="true" />
                  {formatCountdown(verifySecondsLeft)}
                </span>
              )}
            </div>
          )}

          {verifyDeadline && !verificationExpired && (
            <p
              className={cn(
                "tw--mt-2 tw-mb-0 tw-text-xs tw-leading-snug",
                verifySecondsLeft <= 60 ? "tw-text-danger" : "tw-text-content-subtle"
              )}
            >
              Finish your registration before the timer runs out — after that you&apos;ll need
              to verify your phone again.
            </p>
          )}

          <FormField label="Institute Name" htmlFor="ri-institute" required>
            <Input
              id="ri-institute"
              size="lg"
              type="text"
              placeholder="e.g. ABC Academy, TechGuru Classes"
              name="instituteName"
              value={form.instituteName}
              onChange={handleChange}
              required
              autoFocus
              leading={<Building2 size={16} />}
            />
          </FormField>

          {/* Subdomain — optional, with a live availability check. Not a
              FormField: the status line pairs the resolved URL with the check's
              verdict on one row, which the field's single hint slot cannot hold. */}
          <div className="tw-flex tw-flex-col tw-gap-1.5">
            <Label htmlFor="ri-subdomain">Choose your portal URL</Label>
            <Input
              id="ri-subdomain"
              size="lg"
              type="text"
              placeholder="your-institute"
              name="subdomain"
              value={form.subdomain}
              onChange={handleChange}
              maxLength={30}
              leading={<Globe size={16} />}
              invalid={form.subdomain.length >= 3 && subdomainStatus.available === false}
            />
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-3 tw-gap-y-1">
              <span className="tw-text-xs tw-text-content-muted">
                {form.subdomain ? (
                  <>
                    <strong className="tw-text-content">{form.subdomain}</strong>.{PORTAL_DOMAIN}
                  </>
                ) : (
                  `your-institute.${PORTAL_DOMAIN}`
                )}
              </span>
              {form.subdomain.length >= 3 && (
                <span
                  className={cn(
                    "tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium",
                    subdomainStatus.checking
                      ? "tw-text-content-muted"
                      : subdomainStatus.available
                        ? "tw-text-success"
                        : "tw-text-danger"
                  )}
                >
                  {subdomainStatus.checking ? (
                    <Spinner size="sm" animation="border" style={{ width: 12, height: 12 }} />
                  ) : subdomainStatus.available === true ? (
                    <CheckCircle2 size={12} aria-hidden="true" />
                  ) : subdomainStatus.available === false ? (
                    <XCircle size={12} aria-hidden="true" />
                  ) : null}
                  {subdomainStatus.message}
                </span>
              )}
            </div>
            <span className="tw-text-xs tw-leading-snug tw-text-content-subtle">
              Optional — leave empty to auto-generate from your institute name.
            </span>
          </div>

          {/* Two-up from `sm`: these four are short fields and the details step is
              the longest form in the auth family, so pairing them keeps the submit
              button reachable on a 1280×720 laptop. */}
          <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
            <FormField label="Your Name" htmlFor="ri-name" required>
              <Input
                id="ri-name"
                size="lg"
                type="text"
                placeholder="Your full name"
                name="name"
                autoComplete="name"
                value={form.name}
                onChange={handleChange}
                required
                leading={<User size={16} />}
              />
            </FormField>

            <FormField label="Email Address" htmlFor="ri-email" required>
              <Input
                id="ri-email"
                size="lg"
                type="email"
                placeholder="admin@yourinstitute.com"
                name="email"
                autoComplete="email"
                value={form.email}
                onChange={handleChange}
                required
                leading={<Mail size={16} />}
              />
            </FormField>

            <FormField label="Password" htmlFor="ri-password" required>
              <Input
                id="ri-password"
                size="lg"
                type={showPassword ? "text" : "password"}
                placeholder="Min. 6 characters"
                name="password"
                autoComplete="new-password"
                value={form.password}
                onChange={handleChange}
                required
                minLength={6}
                leading={<Lock size={16} />}
                trailing={
                  <IconButton
                    size="sm"
                    label={showPassword ? "Hide password" : "Show password"}
                    icon={showPassword ? Eye : EyeOff}
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                  />
                }
              />
            </FormField>

            <FormField
              label="Confirm Password"
              htmlFor="ri-confirm"
              required
              error={mismatch ? "Passwords do not match" : null}
            >
              <Input
                id="ri-confirm"
                size="lg"
                type={showConfirm ? "text" : "password"}
                placeholder="Re-enter your password"
                name="confirmPassword"
                autoComplete="new-password"
                value={form.confirmPassword}
                onChange={handleChange}
                required
                invalid={mismatch}
                leading={<Lock size={16} />}
                trailing={
                  <IconButton
                    size="sm"
                    label={showConfirm ? "Hide password" : "Show password"}
                    icon={showConfirm ? Eye : EyeOff}
                    onClick={() => setShowConfirm((v) => !v)}
                    tabIndex={-1}
                  />
                }
              />
            </FormField>
          </div>

          <Checkbox
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            label={
              <>
                I agree to the{" "}
                <Link to="/terms-and-conditions" target="_blank" className="tw-text-primary">
                  Terms &amp; Conditions
                </Link>
              </>
            }
          />

          {feedback}

          <AppButton
            type="submit"
            size="lg"
            className="tw-h-12 tw-w-full"
            disabled={loading || verificationExpired}
          >
            {loading ? (
              <>
                <Spinner size="sm" animation="border" /> Creating your institute…
              </>
            ) : (
              <>
                <Rocket size={17} aria-hidden="true" /> {referrer ? "Create Institute" : "Start Free Trial"}
              </>
            )}
          </AppButton>
        </form>
      )}

      <p className="tw-mb-0 tw-mt-6 tw-text-center tw-text-sm tw-text-content-muted">
        Already have an account?{" "}
        <Link
          to="/login"
          className="tw-font-semibold tw-text-primary tw-no-underline hover:tw-underline"
        >
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
};

export default RegisterInstitute;
